import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import type { FilterChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { AgGridReactProps } from 'ag-grid-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { PrimaryContext } from '@/services/context';
import {
  type OnFarmImplantApiSourceType,
  type OnFarmImplantQueueRecord,
  onFarmImplantQueueOptions,
} from '@/services/on-farm-implant';
import { useContextMenu } from '@/utils/context-menu';

import {
  IMPLANT_QUEUE_SOURCE_TYPES,
  useImplantQueueColumnDefs,
  useSourceTypeLabel,
} from './columnDefs';

const ALL_SOURCES = 'all';

type SourceFilter = OnFarmImplantApiSourceType | typeof ALL_SOURCES;

type ImplantQueueTabProps = {
  sessionId: string | null;
};

export const ImplantQueueTab = ({ sessionId }: ImplantQueueTabProps) => {
  const { t } = useTranslation();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'sessionQueueId' });
  const gridRef = useRef<AgGridReact>(null);
  const sourceTypeLabel = useSourceTypeLabel();

  const [searchText, setSearchText] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>(ALL_SOURCES);

  const { data, isLoading, isError, refetch } = useQuery({
    ...onFarmImplantQueueOptions({ sessionId: sessionId ?? '' }),
    enabled: !!sessionId,
  });

  const columnDefs = useImplantQueueColumnDefs();

  const rows = useMemo(() => data?.items ?? [], [data]);

  const countsBySourceType = useMemo(() => {
    const counts = new Map<OnFarmImplantApiSourceType, number>();
    for (const row of rows) {
      counts.set(row.sourceType, (counts.get(row.sourceType) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  const availableSourceTypes = useMemo(
    () =>
      IMPLANT_QUEUE_SOURCE_TYPES.filter((sourceType) =>
        countsBySourceType.has(sourceType),
      ),
    [countsBySourceType],
  );

  const handleSourceFilterChange = useCallback(
    (nextFilter: SourceFilter) => {
      setSourceFilter(nextFilter);

      const api = gridRef.current?.api;
      if (!api) return;

      const model = { ...api.getFilterModel() };

      if (nextFilter === ALL_SOURCES) {
        delete model.sourceType;
      } else {
        model.sourceType = {
          filterType: 'set',
          values: [sourceTypeLabel(nextFilter)],
        };
      }

      api.setFilterModel(model);
    },
    [sourceTypeLabel],
  );

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      const model = event.api.getFilterModel().sourceType as
        | { values?: string[] }
        | undefined;
      const values = model?.values;

      if (!values || values.length !== 1) {
        setSourceFilter(ALL_SOURCES);
        return;
      }

      const matched = IMPLANT_QUEUE_SOURCE_TYPES.find(
        (sourceType) => sourceTypeLabel(sourceType) === values[0],
      );

      setSourceFilter(matched ?? ALL_SOURCES);
    },
    [sourceTypeLabel],
  );

  if (!sessionId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-clock mb-3 text-4xl" />
        <p className="text-sm">{t('onFarmImplant.implantQueue.noSession')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <i className="pi pi-spin pi-spinner text-sm" />
        {t('onFarmImplant.implantQueue.loading')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-gray-500">
        <i className="pi pi-exclamation-triangle text-3xl text-amber-500" />
        <p className="text-sm">{t('onFarmImplant.implantQueue.loadError')}</p>
        <Button
          onClick={() => {
            void refetch();
          }}
        >
          {t('onFarmImplant.implantQueue.retry')}
        </Button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-clock mb-3 text-4xl" />
        <p className="text-base font-medium">
          {t('onFarmImplant.implantQueue.emptyTitle')}
        </p>
        <p className="mt-1 text-sm">
          {t('onFarmImplant.implantQueue.emptyBody')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="relative w-56">
            <i className="pi pi-search absolute top-1/2 left-2 -translate-y-1/2 text-xs text-gray-400" />
            <Input
              placeholder={t('onFarmImplant.implantQueue.filterPlaceholder')}
              className="h-7 pl-7 text-xs"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.implantQueue.queueCount', {
              count: data?.totalRecords ?? rows.length,
            })}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.implantQueue.sourceFilterLabel')}
          </span>
          <button
            type="button"
            aria-pressed={sourceFilter === ALL_SOURCES}
            onClick={() => handleSourceFilterChange(ALL_SOURCES)}
            className={clsx(
              'rounded-full border px-3 py-1 text-xs font-medium',
              sourceFilter === ALL_SOURCES
                ? 'border-blue-600 bg-blue-600 text-white'
                : 'border-gray-300 bg-white text-gray-700',
            )}
          >
            {t('onFarmImplant.implantQueue.allSources')}
          </button>
          {availableSourceTypes.map((sourceType) => (
            <button
              key={sourceType}
              type="button"
              aria-pressed={sourceFilter === sourceType}
              onClick={() => handleSourceFilterChange(sourceType)}
              className={clsx(
                'rounded-full border px-3 py-1 text-xs font-medium',
                sourceFilter === sourceType
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-gray-300 bg-white text-gray-700',
              )}
            >
              {sourceTypeLabel(sourceType)} (
              {countsBySourceType.get(sourceType) ?? 0})
            </button>
          ))}
        </div>
      </div>

      <div
        className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[calc(100vh-13rem)] min-h-[400px] w-full`}
      >
        <WithLocalStorage storageKey="on-farm-implant-implant-queue">
          <AgGridReact
            ref={gridRef}
            gridOptions={gridOptions}
            columnDefs={columnDefs as AgGridReactProps['columnDefs']}
            defaultColDef={defaultColDef}
            rowData={rows}
            headerHeight={36}
            rowHeight={48}
            quickFilterText={searchText}
            getRowId={getRowId<OnFarmImplantQueueRecord>(
              (params) => params.data.sessionQueueId,
            )}
            getContextMenuItems={getContextMenuItems}
            onFilterChanged={handleFilterChanged}
          />
        </WithLocalStorage>
      </div>
    </div>
  );
};
